import React, { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { History, ChevronLeft, ChevronRight, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ENTITY_FILTERS = [
  { value: "all", label: "All entities" },
  { value: "user", label: "Users" },
  { value: "location", label: "Locations" },
  { value: "payment", label: "Payments" },
  { value: "session", label: "Sessions" },
  { value: "expense", label: "Expenses" },
  { value: "post", label: "Posts" },
  { value: "match", label: "Matches" },
];

const ACTION_FILTERS = [
  { value: "all", label: "All actions" },
  { value: "CREATE", label: "Created" },
  { value: "UPDATE", label: "Updated" },
  { value: "DELETE", label: "Deleted" },
  { value: "SETTLE", label: "Settled" },
  { value: "RECORD", label: "Recorded" },
];

const ACTION_STYLE = {
  CREATE: "bg-emerald-100 text-emerald-800 border-emerald-200",
  UPDATE: "bg-amber-100 text-amber-800 border-amber-200",
  DELETE: "bg-red-100 text-red-800 border-red-200",
  SETTLE: "bg-indigo-100 text-indigo-800 border-indigo-200",
  RECORD: "bg-blue-100 text-blue-800 border-blue-200",
};

const PAGE_SIZE = 25;

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return iso;
  }
};

export const AuditLogTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [entityType, setEntityType] = useState("all");
  const [action, setAction] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
        if (entityType !== "all") params.set("entity_type", entityType);
        if (action !== "all") params.set("action", action);
        const res = await apiClient.get(`/admin/audit-logs?${params.toString()}`);
        if (cancelled) return;
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } catch (err) {
        if (cancelled) return;
        console.error("Audit log fetch failed:", err);
        toast.error("Failed to load activity log");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLogs();
    return () => { cancelled = true; };
  }, [entityType, action, offset]);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (entityType !== "all") params.set("entity_type", entityType);
      if (action !== "all") params.set("action", action);
      const res = await apiClient.get(`/admin/audit-logs?${params.toString()}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Audit refresh failed:", err);
      toast.error("Failed to load activity log");
    } finally {
      setLoading(false);
    }
  };

  const onFilterChange = (setter) => (val) => { setter(val); setOffset(0); };
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + items.length, total);
  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  return (
    <Card className="rounded-2xl border-slate-200" data-testid="audit-log-card">
      <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <CardTitle className="font-heading flex items-center gap-2">
            <History className="w-5 h-5 text-[#064E3B]" />
            Activity Log
          </CardTitle>
          <CardDescription>
            Every admin write action is recorded here for accountability.
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={entityType} onValueChange={onFilterChange(setEntityType)}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="audit-filter-entity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ENTITY_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={action} onValueChange={onFilterChange(setAction)}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="audit-filter-action">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTION_FILTERS.map((f) => (
                <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline" size="sm"
            onClick={handleRefresh} disabled={loading}
            data-testid="audit-refresh-btn"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && items.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading activity…
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-slate-500" data-testid="audit-empty">
            <History className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            No activity recorded yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="audit-log-list">
            {items.map((it) => (
              <div
                key={it.id}
                data-testid={`audit-log-row-${it.id}`}
                className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:border-emerald-300 transition-colors"
              >
                <div className="w-9 h-9 shrink-0 rounded-full bg-[#064E3B] text-white text-xs font-bold flex items-center justify-center">
                  {(it.admin_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wide ${ACTION_STYLE[it.action] || ""}`}>
                      {it.action}
                    </Badge>
                    <span className="text-xs text-slate-500 uppercase tracking-wide">
                      {it.entity_type}
                    </span>
                    <span className="text-xs text-slate-400">·</span>
                    <span className="text-xs text-slate-500">{formatTime(it.timestamp)}</span>
                  </div>
                  <p className="text-sm text-slate-800 mt-1 leading-snug">
                    <span className="font-medium">{it.admin_name || "Unknown"}</span>{" "}
                    <span className="text-slate-600">— {it.summary}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
          <span className="text-xs text-slate-500" data-testid="audit-pagination-info">
            Showing {pageStart}–{pageEnd} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              disabled={!canPrev || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              data-testid="audit-prev-page"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <Button
              variant="outline" size="sm"
              disabled={!canNext || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              data-testid="audit-next-page"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AuditLogTab;
